import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-6 py-6 text-center">
          <div className="flex items-center justify-center gap-4">
            <img 
              src="https://public.readdy.ai/ai/img_res/80e0ac67-a2b7-472a-ba27-b5c8fedde2ab.png" 
              alt="DARMAR d.o.o. Logo" 
              className="h-10 w-auto"
            />
            <h1 className="text-2xl font-medium text-[#2C3E50]">DARMAR d.o.o.</h1>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-40 pb-32 bg-gradient-to-b from-white to-[#F8FBFF]">
        <div className="container mx-auto px-6">
          <div className="flex items-center gap-16">
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-8">
                (iOS Development Experts)
              </p>
              <h2 className="text-7xl font-light text-[#1A2332] leading-tight mb-8">
                Building Premium<br />
                <span className="inline-flex items-center gap-4">
                  iOS Applications
                  <img 
                    src="https://public.readdy.ai/ai/img_res/0ac8c598eb8a21ae3fb52301d0e2f61d.jpg" 
                    alt="iOS Interface" 
                    className="w-20 h-20 rounded-full shadow-lg object-cover"
                  />
                </span>
                <br />
                for Modern Users
              </h2>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="max-w-md">
                <p className="text-lg leading-relaxed text-[#5A6C7D]">
                  DARMAR d.o.o. is a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl ml-[15%]">
            <p className="text-xs uppercase tracking-widest text-[#7DD3FC] mb-6">
              (About Our Team)
            </p>
            <div className="mb-16">
              <h3 className="text-6xl font-light text-[#1A2332] leading-tight mb-2">
                <span className="inline-flex items-center gap-6">
                  Expertise in iOS
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20software%20development%20team%20collaboration%20scene%20with%20soft%20blue%20tones%20modern%20office%20workspace%20developers%20working%20together%20clean%20minimalist%20aesthetic%20technology%20innovation%20atmosphere&width=180&height=120&seq=about-team-1&orientation=landscape" 
                    alt="Team Collaboration" 
                    className="w-32 h-20 rounded-2xl object-cover shadow-md"
                  />
                </span>
                <br />
                Development
              </h3>
            </div>
            <div className="max-w-2xl space-y-6">
              <p className="text-base leading-relaxed text-[#4A5568]">
                Our team consists of highly skilled iOS developers, designers, and security experts who are passionate about creating exceptional mobile experiences. With years of combined experience in the Apple ecosystem, we understand the nuances of iOS development and the expectations of modern users.
              </p>
              <p className="text-base leading-relaxed text-[#4A5568]">
                We believe in building applications that not only look beautiful but also perform flawlessly. Every line of code we write is crafted with attention to detail, ensuring optimal performance, security, and user satisfaction. Our development philosophy centers around creating intuitive interfaces that users love.
              </p>
              <p className="text-base leading-relaxed text-[#4A5568]">
                At DARMAR d.o.o., we are committed to staying at the forefront of iOS technology. We continuously explore new frameworks, design patterns, and security protocols to deliver cutting-edge solutions that meet the evolving needs of mobile users worldwide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-gradient-to-b from-[#F7F9FC] to-white">
        <div className="container mx-auto px-6">
          <div className="flex items-start justify-between mb-24">
            <div className="flex-1">
              <h3 className="text-6xl font-bold text-[#1A2332] leading-tight">
                Our iOS<br />Products
              </h3>
            </div>
            <div className="flex-1">
              <p className="text-base text-[#6B7280] leading-relaxed max-w-md">
                We specialize in developing high-performance iOS applications that prioritize user security, privacy, and device optimization. Each product is meticulously designed to deliver exceptional value.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-10 gap-8">
            {/* VPN Application - Wider Card */}
            <div className="col-span-4 bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="h-80 bg-gradient-to-br from-[#7DD3FC] to-[#3B82F6] flex items-center justify-center p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iPhone%20mockup%20displaying%20VPN%20application%20interface%20with%20security%20shield%20icon%20blue%20gradient%20background%20clean%20minimalist%20design%20connected%20status%20indicator%20professional%20mobile%20app%20screenshot&width=280&height=560&seq=product-vpn-1&orientation=portrait" 
                  alt="VPN Application" 
                  className="w-48 h-auto object-contain drop-shadow-2xl"
                />
              </div>
              <div className="p-8">
                <h4 className="text-2xl font-semibold text-[#1A2332] mb-4">VPN Application</h4>
                <p className="text-sm leading-relaxed text-[#6B7280]">
                  Our VPN application provides robust security and privacy protection for iOS users. With military-grade encryption, fast connection speeds, and a user-friendly interface, it ensures your online activities remain private and secure. Features include automatic kill switch, no-logs policy, and global server coverage.
                </p>
              </div>
            </div>

            {/* Cleaner Application */}
            <div className="col-span-3 bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="h-64 bg-gradient-to-br from-[#A78BFA] to-[#8B5CF6] flex items-center justify-center p-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20showing%20cleaner%20application%20interface%20with%20storage%20optimization%20charts%20purple%20gradient%20background%20clean%20modern%20design%20file%20management%20icons%20mobile%20app%20screenshot&width=240&height=480&seq=product-cleaner-1&orientation=portrait" 
                  alt="Cleaner Application" 
                  className="w-36 h-auto object-contain drop-shadow-2xl"
                />
              </div>
              <div className="p-6">
                <h4 className="text-xl font-semibold text-[#1A2332] mb-3 text-center">Cleaner Application</h4>
                <p className="text-sm leading-relaxed text-[#6B7280]">
                  Optimize your iOS device performance with our intelligent cleaner app. Remove duplicate photos, clear cache, manage storage, and boost device speed with just a few taps.
                </p>
              </div>
            </div>

            {/* Performance Tools */}
            <div className="col-span-3 bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="h-64 bg-gradient-to-br from-[#34D399] to-[#10B981] flex items-center justify-center p-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20displaying%20performance%20optimization%20tools%20interface%20with%20speed%20meter%20green%20gradient%20background%20system%20monitoring%20dashboard%20clean%20modern%20design%20mobile%20app%20screenshot&width=240&height=480&seq=product-performance-1&orientation=portrait" 
                  alt="Performance Tools" 
                  className="w-36 h-auto object-contain drop-shadow-2xl"
                />
              </div>
              <div className="p-6">
                <h4 className="text-xl font-semibold text-[#1A2332] mb-3 text-center">Performance Tools</h4>
                <p className="text-sm leading-relaxed text-[#6B7280]">
                  Advanced performance monitoring and optimization tools that help you keep your iOS device running at peak efficiency. Real-time analytics and smart recommendations included.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex items-center gap-16">
            <div className="flex-1">
              <div className="rounded-[32px] overflow-hidden bg-gradient-to-br from-[#7DD3FC] to-[#C084FC] p-4 h-96">
                <img 
                  src="https://readdy.ai/api/search-image?query=abstract%20illustration%20of%20mobile%20devices%20with%20security%20shield%20iconography%20soft%20gradient%20background%20from%20sky%20blue%20to%20light%20purple%20modern%20minimalist%20design%20technology%20protection%20concept%20clean%20aesthetic&width=600&height=500&seq=testimonial-visual-1&orientation=landscape" 
                  alt="Security Illustration" 
                  className="w-full h-full object-cover rounded-3xl"
                />
              </div>
            </div>
            <div className="flex-1 pl-16">
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-6">
                (Client Feedback)
              </p>
              <h3 className="text-5xl font-medium text-[#1A2332] leading-tight mb-20">
                Trusted by Users<br />
                <span className="text-gray-400">Worldwide</span>
              </h3>
              <div className="relative">
                <div className="text-6xl text-gray-200 leading-none mb-4">"</div>
                <p className="text-lg leading-relaxed text-[#4A5568] mb-8">
                  The applications developed by DARMAR d.o.o. demonstrate exceptional quality and attention to detail. Their VPN solution provides robust security while maintaining excellent performance.
                </p>
                <div className="text-6xl text-gray-200 leading-none text-right mb-4">"</div>
                <p className="text-base font-bold text-[#1A2332]">— iOS User Review</p>
              </div>
              <div className="flex gap-4 mt-12">
                <button className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-gray-400 transition-colors cursor-pointer">
                  <i className="ri-arrow-left-s-line text-gray-600"></i>
                </button>
                <button className="w-10 h-10 rounded-full bg-[#1A2332] flex items-center justify-center hover:bg-[#2C3E50] transition-colors cursor-pointer">
                  <i className="ri-arrow-right-s-line text-white"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Footer Section */}
      <footer id="contact" className="bg-[#1A2E3D] text-white">
        <div className="container mx-auto px-6 py-20">
          <div className="grid grid-cols-4 gap-12 mb-20">
            <div>
              <h4 className="text-sm uppercase tracking-wider text-[#7DD3FC] mb-4 pb-3 border-b border-white/10">
                Company
              </h4>
              <ul className="space-y-4">
                <li className="text-[15px] text-[#CBD5E1] hover:text-white transition-colors cursor-pointer">About</li>
                <li className="text-[15px] text-[#CBD5E1] hover:text-white transition-colors cursor-pointer">Team</li>
                <li className="text-[15px] text-[#CBD5E1] hover:text-white transition-colors cursor-pointer">Expertise</li>
                <li className="text-[15px] text-[#CBD5E1] hover:text-white transition-colors cursor-pointer">Products</li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-[#7DD3FC] mb-4 pb-3 border-b border-white/10">
                Products
              </h4>
              <ul className="space-y-4">
                <li className="text-[15px] text-[#CBD5E1] hover:text-white transition-colors cursor-pointer">VPN App</li>
                <li className="text-[15px] text-[#CBD5E1] hover:text-white transition-colors cursor-pointer">Cleaner App</li>
                <li className="text-[15px] text-[#CBD5E1] hover:text-white transition-colors cursor-pointer">Tools</li>
                <li className="text-[15px] text-[#CBD5E1] hover:text-white transition-colors cursor-pointer">Features</li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-[#7DD3FC] mb-4 pb-3 border-b border-white/10">
                Support
              </h4>
              <ul className="space-y-4">
                <li className="text-[15px] text-[#CBD5E1] hover:text-white transition-colors cursor-pointer">Documentation</li>
                <li className="text-[15px] text-[#CBD5E1] hover:text-white transition-colors cursor-pointer">Resources</li>
                <li className="text-[15px] text-[#CBD5E1] hover:text-white transition-colors cursor-pointer">Updates</li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-[#7DD3FC] mb-4 pb-3 border-b border-white/10">
                Legal
              </h4>
              <ul className="space-y-4">
                <li className="text-[15px] text-[#CBD5E1] hover:text-white transition-colors cursor-pointer">Privacy</li>
                <li className="text-[15px] text-[#CBD5E1] hover:text-white transition-colors cursor-pointer">Terms</li>
                <li className="text-[15px] text-[#CBD5E1] hover:text-white transition-colors cursor-pointer">Security</li>
              </ul>
            </div>
          </div>

          <div className="flex items-start justify-between pt-16 border-t border-white/10">
            <div className="flex-1">
              <h2 className="text-7xl font-bold text-white mb-6">DARMAR d.o.o.</h2>
              <div className="flex items-center gap-4">
                <div className="w-20 h-0.5 bg-white"></div>
                <p className="text-[13px] text-gray-400">iOS Development Excellence</p>
              </div>
            </div>
            <div className="flex-1 text-right">
              <p className="text-[13px] text-gray-400 mb-12">© 2025 DARMAR d.o.o.</p>
              <div className="space-y-5 inline-block text-left">
                <div className="flex items-center gap-3">
                  <i className="ri-map-pin-line text-[#7DD3FC] w-4 h-4 flex items-center justify-center"></i>
                  <div>
                    <strong className="text-[11px] uppercase text-[#7DD3FC] tracking-wider">Address</strong>
                    <p className="text-sm text-white">Molizanskih Hrvata 64</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-building-line text-[#7DD3FC] w-4 h-4 flex items-center justify-center"></i>
                  <div>
                    <strong className="text-[11px] uppercase text-[#7DD3FC] tracking-wider">City</strong>
                    <p className="text-sm text-white">Makarska</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line text-[#7DD3FC] w-4 h-4 flex items-center justify-center"></i>
                  <div>
                    <strong className="text-[11px] uppercase text-[#7DD3FC] tracking-wider">Zip</strong>
                    <p className="text-sm text-white">21300</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-global-line text-[#7DD3FC] w-4 h-4 flex items-center justify-center"></i>
                  <div>
                    <strong className="text-[11px] uppercase text-[#7DD3FC] tracking-wider">Country</strong>
                    <p className="text-sm text-white">Croatia</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-mail-send-line text-[#7DD3FC] w-4 h-4 flex items-center justify-center"></i>
                  <div>
                    <strong className="text-[11px] uppercase text-[#7DD3FC] tracking-wider">Email</strong>
                    <p className="text-sm text-white">info@darmardoo.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-[#7DD3FC] w-4 h-4 flex items-center justify-center"></i>
                  <div>
                    <strong className="text-[11px] uppercase text-[#7DD3FC] tracking-wider">Number</strong>
                    <p className="text-sm text-white">+1 3023376456</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-white/10 text-center">
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-sm text-gray-400 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
